// Parses AI JSON responses and executes operations through the command kernel.
// The in-app chat is a consumer of the same API as MCP — never a second one.

import { COMMAND_NAME_SET, invoke, isCommandError } from '../../agent';
import type { AIOperation, AIOperationResult, AIResponse } from './types';

/** Parse the AI model's raw text response into a structured AIResponse. */
export function parseAIResponse(raw: string): AIResponse {
  let cleaned = raw.trim();
  if (cleaned.startsWith('```json')) cleaned = cleaned.slice(7);
  else if (cleaned.startsWith('```')) cleaned = cleaned.slice(3);
  if (cleaned.endsWith('```')) cleaned = cleaned.slice(0, -3);
  cleaned = cleaned.trim();

  try {
    const parsed = JSON.parse(cleaned);
    return {
      thinking: typeof parsed.thinking === 'string' ? parsed.thinking : undefined,
      operations: Array.isArray(parsed.operations) ? parsed.operations : [],
      reply: typeof parsed.reply === 'string' ? parsed.reply : 'Done.',
    };
  } catch {
    return {
      operations: [],
      reply: cleaned || 'I could not generate a valid response. Please try rephrasing.',
    };
  }
}

function validateOperation(op: AIOperation): string | null {
  if (!op.method || typeof op.method !== 'string') {
    return 'Missing or invalid method name';
  }
  if (!COMMAND_NAME_SET.has(op.method)) {
    return `Method "${op.method}" is not allowed`;
  }
  if (op.params !== undefined && op.params !== null && typeof op.params !== 'object') {
    return `Invalid params for ${op.method}: expected object`;
  }
  return null;
}

/** Execute a batch of AI operations against the command kernel. */
export async function executeOperations(operations: AIOperation[]): Promise<AIOperationResult[]> {
  const results: AIOperationResult[] = [];

  for (const op of operations) {
    const validationError = validateOperation(op);
    if (validationError) {
      results.push({ method: op.method, success: false, error: validationError });
      continue;
    }

    try {
      let result: unknown = invoke(op.method, op.params ?? {});
      if (result && typeof (result as Promise<unknown>).then === 'function') {
        result = await (result as Promise<unknown>);
      }
      if (isCommandError(result)) {
        results.push({ method: op.method, success: false, error: result.error, result });
      } else {
        results.push({ method: op.method, success: true, result });
      }
    } catch (err) {
      results.push({
        method: op.method,
        success: false,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  return results;
}
